import { AssessmentDomainStatus, FileStatus } from '@libs/constants';
import { hasValidClassification } from '@libs/utils';

import type { AssessmentDocumentViewModel } from '../types';

/**
 * Pure selectors for the assessment workflow page.
 *
 * Co-located so "what disables Save?" / "what blocks navigation?" is answerable
 * in a single file. All functions here are pure — easy to unit-test.
 */

export type SaveBlockReason =
  | 'uploading'
  | 'validation-error'
  | 'classifying'
  | 'classification-failed'
  | 'no-valid-doc';

/**
 * Domain is editable iff its status is IN_PROGRESS. NOT_STARTED and COMPLETED
 * domains are read-only until the user explicitly starts / re-opens them.
 */
export const getIsDomainEditable = (
  status: AssessmentDomainStatus | undefined,
): boolean => status === AssessmentDomainStatus.IN_PROGRESS;

/**
 * Active files = uploaded / not-yet-saved files that haven't been (soft-)deleted.
 * The classification check operates on this subset.
 */
export const getActiveFiles = (
  files: AssessmentDocumentViewModel[],
): AssessmentDocumentViewModel[] =>
  files.filter(
    (f) =>
      (f.status === FileStatus.UPLOADED || f.status === FileStatus.NOT_SAVED) &&
      !f.deleted,
  );

export const getIsAnyFileUploading = (
  files: AssessmentDocumentViewModel[],
): boolean =>
  files.some(
    (f) => f.status === FileStatus.QUEUED || f.status === FileStatus.UPLOADING,
  );

export const getIsAnyFileDeleting = (
  files: AssessmentDocumentViewModel[],
): boolean =>
  files.some(
    (f) =>
      f.status === FileStatus.PENDING_DELETE || f.status === FileStatus.DELETING,
  );

/**
 * Count of documents that visually occupy a slot in the UI:
 * anything uploaded / not-saved / mid-upload / mid-delete still counts toward
 * the "no evidence" validation until it settles into a deleted state.
 */
export const getUploadedDocumentsCount = (
  files: AssessmentDocumentViewModel[],
): number =>
  files.filter(
    (f) =>
      f.status === FileStatus.UPLOADED ||
      f.status === FileStatus.NOT_SAVED ||
      f.status === FileStatus.PENDING_DELETE ||
      f.status === FileStatus.UPLOADING ||
      f.status === FileStatus.DELETING,
  ).length;

export const getHasAtLeastOneValidDocument = (
  activeFiles: AssessmentDocumentViewModel[],
): boolean =>
  activeFiles.some((f) =>
    hasValidClassification({ classificationMetadata: f.classificationMetadata ?? null }),
  );

export interface SaveBlockInputs {
  files: AssessmentDocumentViewModel[];
  validationError: string | null;
  /** Domain is in a state where classification validation should run (IN_PROGRESS + applicable + has evidence). */
  isClassificationValidationActive: boolean;
  isAnyClassifying: boolean;
  isAnyClassificationFailed: boolean;
}

/**
 * Single source of truth for "why is Save blocked?".
 *
 * Priority order — only the highest-priority reason is returned so the banner
 * can render a single, unambiguous message:
 *   1. uploading            — let file uploads settle first
 *   2. validation-error     — generic save-time validation failure
 *   3. classifying          — wait for classification to finish
 *   4. classification-failed — user must retry / remove failed docs
 *   5. no-valid-doc         — at least one valid doc required
 *
 * Note: in-progress deletions block navigation (see `getIsAnyFileDeleting`)
 * but intentionally do not surface a banner — matching prior UX.
 */
export const getSaveBlockReason = ({
  files,
  validationError,
  isClassificationValidationActive,
  isAnyClassifying,
  isAnyClassificationFailed,
}: SaveBlockInputs): SaveBlockReason | null => {
  if (getIsAnyFileUploading(files)) return 'uploading';
  if (validationError) return 'validation-error';

  if (!isClassificationValidationActive) return null;

  if (isAnyClassifying) return 'classifying';
  if (isAnyClassificationFailed) return 'classification-failed';

  const activeFiles = getActiveFiles(files);
  if (activeFiles.length > 0 && !getHasAtLeastOneValidDocument(activeFiles)) {
    return 'no-valid-doc';
  }

  return null;
};
